import { Refresh } from "@mui/icons-material";
import { Box, Button, Snackbar } from "@mui/material";
import { memo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { isProcoreSceneReady, onSceneBackfilling } from "apis/procore";
import type { SceneReadBackend } from "apis/procore/errors";
import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { useRefreshAndRestoreState } from "hooks/useRefreshAndRestoreState";
import { explorerActions, selectProcoreContext, selectSceneDbBackfill, selectSceneId } from "slices/explorer";

/** How often to re-probe the scene while a backfill is in progress. */
const RECOVERY_POLL_INTERVAL_MS = 15_000;

/**
 * Global surface for the BIM Search DB `202` (scene backfilling) state.
 *
 * The search-DB client emits a signal whenever any object/search/property call
 * returns 202 (scene database missing, backfill from FAS in progress). This shows
 * a non-error "preparing" banner (no action, nothing to reload yet) and, when the
 * scene ids are known, polls the backend that reported the miss until the scene is ready. On
 * ready it switches the banner to a "ready, reload to view" state with a Refresh
 * (reload + restore) button — the app never reloads without the user asking.
 */
export default memo(function SceneBackfillSnackbar() {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const backfill = useAppSelector(selectSceneDbBackfill);
    const [backend, setBackend] = useState<SceneReadBackend>("legacy");
    const procoreContext = useAppSelector(selectProcoreContext);
    const sceneId = useAppSelector(selectSceneId);
    const refresh = useRefreshAndRestoreState();

    // Enter the "preparing" state whenever a search-DB call reports a 202.
    useEffect(
        () =>
            onSceneBackfilling((readBackend) => {
                setBackend(readBackend);
                dispatch(explorerActions.setSceneDbBackfill({ preparing: true, ready: false }));
            }),
        [dispatch],
    );

    // While preparing, poll until the backfill completes, then switch to the ready state.
    useEffect(() => {
        if (!backfill.preparing) {
            return;
        }

        const probe = resolveProbeIds(procoreContext, sceneId);
        if (!probe) {
            // Ids unavailable (e.g. non-Procore backend): keep the banner and rely on manual Refresh.
            return;
        }

        const controller = new AbortController();
        const timer = setInterval(async () => {
            try {
                const ready = await isProcoreSceneReady({ ...probe, backend, signal: controller.signal });
                if (ready) {
                    // Don't reload on the user's behalf — surface a "ready, reload to view"
                    // banner and let them choose when to reload (agreed with design).
                    dispatch(explorerActions.setSceneDbBackfill({ preparing: false, ready: true }));
                }
            } catch {
                // Transient/other errors: keep polling until the scene is ready.
            }
        }, RECOVERY_POLL_INTERVAL_MS);

        return () => {
            controller.abort();
            clearInterval(timer);
        };
    }, [backfill.preparing, backend, procoreContext, sceneId, dispatch]);

    if (!backfill.preparing && !backfill.ready) {
        return null;
    }

    return (
        <Snackbar
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            sx={{
                width: { xs: "auto", sm: 400 },
                bottom: { xs: "auto", sm: 88 },
                top: { xs: 24, sm: "auto" },
                pointerEvents: "auto",
            }}
            open={true}
            message={
                backfill.ready
                    ? t("sceneDatabaseReady", "The scene database is ready. Reload to view it.")
                    : t("sceneDatabasePreparing", "The scene database is being prepared. This can take a few minutes.")
            }
            // While preparing there is no user action (nothing to reload yet); the reload
            // button appears only once the DB is ready, so reloading is always user-initiated.
            action={
                backfill.ready ? (
                    <Box display="flex" alignItems="center" gap={1}>
                        <Button
                            color="primary"
                            size="small"
                            variant="contained"
                            type="button"
                            onClick={refresh}
                            startIcon={<Refresh />}
                        >
                            {t("refresh")}
                        </Button>
                    </Box>
                ) : undefined
            }
        />
    );
});

/**
 * The scene being backfilled is the one currently open, so its ids come from the
 * same state the search-DB calls use. `procoreContext.sceneVersionId` is optional
 * on input but is published back to the store once resolved during init
 * (see `useHandleInit`), so it is available here by the time a 202 can occur.
 */
function resolveProbeIds(
    procoreContext: { companyId: number; projectId: number; sceneVersionId?: string } | null,
    sceneId: string | null,
): { companyId: number; projectId: number; sceneId: string; sceneVersionId: string } | undefined {
    if (procoreContext == null || !sceneId || !procoreContext.sceneVersionId) {
        return undefined;
    }

    return {
        companyId: procoreContext.companyId,
        projectId: procoreContext.projectId,
        sceneId,
        sceneVersionId: procoreContext.sceneVersionId,
    };
}
